import { useState, useRef, useEffect } from 'react';
import { UseFormRegisterReturn, FieldValues, Path } from 'react-hook-form';

import { DropdownProps } from './types';

import IconChevron from '@/assets/icons/iconChevron.svg';
import { classNames } from '@/shared/lib/classNames/classNames';

export const Dropdown = <T extends FieldValues>({
  label,
  options,
  error,
  watch,
  onChange,
  className,
  ...inputProps
}: DropdownProps<T> & Omit<UseFormRegisterReturn, 'onChange'>) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectedValue = watch(inputProps.name as Path<T>);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleDocumentClick = (event: Event) => {
      if (!dropdownRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('click', handleDocumentClick);
    }

    return () => document.removeEventListener('click', handleDocumentClick);
  }, [isOpen]);

  return (
    <div
      ref={dropdownRef}
      className={classNames({
        cls: 'relative w-[400px]',
        additional: [className],
      })}
    >
      <div className="relative h-14">
        <div
          onClick={() => setIsOpen((isOpen) => !isOpen)}
          {...inputProps}
          id={label}
          className={classNames({
            cls: 'peer bg-custom-surface-contrast-low flex h-full w-full cursor-pointer rounded-xl border border-transparent px-3 py-2 pt-6 text-base focus:outline-none',
            mods: {
              '!border-status-errorSecondary bg-status-errorTertiary': !!error,
              'hover:bg-surface-base-inverse-tertiary': !error,
            },
          })}
        >
          {options.find(({ value }) => value === selectedValue)?.label}
          <IconChevron
            className={classNames({
              cls: 'absolute top-[1.125rem] right-[1.125rem] bottom-[1.125rem] transition-transform',
              mods: { 'rotate-180': isOpen },
            })}
          />
        </div>
        <label
          htmlFor={label}
          className={classNames({
            cls: 'text-text-base-secondary pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 cursor-text text-base tracking-[-0.01rem] transition-all duration-300 peer-focus:top-4',
            mods: {
              'top-4 text-sm leading-[18px]': selectedValue,
              'peer-hover:text-text-base-primary': !selectedValue,
            },
          })}
        >
          {label}
        </label>
      </div>
      {!!error?.message && (
        <span className="text-status-errorSecondary mt-1 block text-[12px] leading-[18px] font-normal">
          {error?.message}
        </span>
      )}
      {isOpen && (
        <div className="bg-surface-base-inverse-primary2 absolute top-16 z-20 w-[400px] rounded-xl max-sm:w-full">
          <ul className="bg-custom-surface-contrast-low w-full rounded-xl py-2">
            {options.map(({ value, label }) => (
              <li
                onClick={() => {
                  onChange(value);
                  setIsOpen(false);
                }}
                className="text-surface-base-primary hover:bg-surface-base-inverse-secondary flex h-[32px] cursor-pointer items-center px-3 text-[16px]"
                key={value}
              >
                {label}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
